import useProductQuery from '@hooks/product/use-product-query';
import { ParsedProductFeed, useRecommendProductFeeds } from '@hooks/product/use-recommend-product-feed';
import PdpProductFeed from '@components/product/pdp-product-feed/pdp-product-feed';
import React, { useRef } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useIntersectionObserver } from 'usehooks-ts';
import { useTranslation } from 'next-i18next';
import { ProductFeedSource } from '@interfaces/api/payloads/product-feed-payload';

const PdpRecommendationFeed = ({ feed, productId }: { feed: ParsedProductFeed; productId: string }) => {
  const { t } = useTranslation();

  const { sendAnalyticEvent } = useAnalyticEvents('similar_products');
  const similarProductsBlockRef = useRef(null);

  // We can't directly lazy-load/render this block, because it's needed for SEO.
  const shouldRenderSimilarProductsImgs =
    useIntersectionObserver(similarProductsBlockRef, {
      freezeOnceVisible: true,
      threshold: 0.5,
    })?.isIntersecting || false;

  if (!feed.items?.length || !feed.title || !feed.feedType) {
    return null;
  }

  return (
    <div ref={similarProductsBlockRef}>
      <PdpProductFeed
        products={feed.items}
        productFeedCategory={feed.feedType}
        title={feed.title}
        productCardProps={{
          moduleType: 'similar_products',
          pageType: 'product_page',
          showProductDirectShipping: false,
          showProductExpressDelivery: false,
          forceImgLazyLoadingCb: () => shouldRenderSimilarProductsImgs,
        }}
        cta={{
          title: t('PRODUCT.DISCOVER.CTA'),
          sendClickAnalytics: () => sendAnalyticEvent('pdp_reco_view_all', { label: feed.feedType }),
          href: `/similar-products?productId=${productId}&feedType=${feed.feedType}`,
          rel: 'nofollow',
        }}
        testId={`pdp_${feed.feedType}`}
      />
    </div>
  );
};

const PdpRecommendationModules = () => {
  const {
    product: { id: productID },
  } = useProductQuery();
  const { feeds } = useRecommendProductFeeds({
    productID: parseInt(productID, 10),
    source: ProductFeedSource.PDP,
  });

  if (!feeds || feeds.length === 0) {
    return null;
  }

  return (
    <>
      {feeds.map((feed) => (
        <PdpRecommendationFeed
          key={feed.feedType}
          feed={feed}
          productId={productID}
        />
      ))}
    </>
  );
};

export default PdpRecommendationModules;
